import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from 'pdfmake/build/vfs_fonts';
import useDate from "../day";
import figureFormatter from "@/services/utils/figureFormatter";
import { loadImageAsBase64 } from "@/services/utils/global";

pdfMake.vfs = pdfFonts.pdfMake.vfs;

pdfMake.fonts = {
  SulaimanLipi: {
    normal: 'https://fonts.cdnfonts.com/s/14639/solaimanlipi.woff',
    bold: 'https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.66/fonts/Roboto/Roboto-Medium.ttf',
    italics: 'https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.66/fonts/Roboto/Roboto-Italic.ttf',
    bolditalics: 'https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.66/fonts/Roboto/Roboto-MediumItalic.ttf'
  }
};

const printMushak63En = () => {
  const { formatDate } = useDate();
  const { commaFormat } = figureFormatter();

  const exportToPdfMushakEn = async (companyInfo, invoice, user) => {
      const imagePath = await loadImageAsBase64('/app-assets/images/gov_seal.png');

      const doc = {
      pageSize: "A4",
      pageMargins: [30, 85, 30, 30],
      defaultStyle: {
        font: "SulaimanLipi",
        fontSize: 10
      },
      header: getHeader(imagePath),
      footer: getFooter(),
      content: getContent(companyInfo, invoice, user),
      info: {
        title: 'MUSHAK-6.3'
      },
      styles: styles
    };

    pdfMake.createPdf(doc).open();
  }

  const getContent = (companyInfo, invoice, user) => {
    return [
      {
        stack: [
          { text: 'Name of Registered Person: ' + safeText(companyInfo.name), alignment: 'left', fontSize: 10 },
          { text: 'BIN of Registered Person: ' + safeText(companyInfo.vat_no), alignment: 'left', fontSize: 10 },
          { text: 'Challan Issuing Address: ' + safeText(companyInfo.address), alignment: 'left', fontSize: 10 },
        ],
        margin: [0, 0, 0, 10]
      },

      getHeadTable(invoice),
      {
        margin: [0, 10, 0, 10],
        table: {
          headerRows: 2,
          widths: [18, '*', 34, 26, 42, 46, 38, 40, 38, 48, 50],
          body: getTableBody(invoice)
        },
        layout: {
          hLineWidth: () => 0.5,
          vLineWidth: () => 0.5,
          hLineColor: () => '#000',
          vLineColor: () => '#000',
          paddingTop: () => 4,
          paddingBottom: () => 4
        }
      },

      {
        columns: [
          { width: '60%', text: 'Name of organization Officer-in-charge: ' },
          { width: '40%', text: 'Designation: ' }
        ],
        margin: [0, 20, 0, 15]
      },
      {
        columns: [
          { width: '60%', text: 'Signature:' },
          { width: '40%', text: 'Seal:' }
        ],
        margin: [0, 0, 0, 20]
      }
    ];
  }

  const getTableBody = (invoice) => {
    const header = getTableHead();
    const headerNumber = getTableHeadNumber();
    const row = getTableRow(invoice)
    const total = getTableTotal(invoice)
    return [
      header,
      headerNumber,
      ...row,
      total
    ]
  }

  const getLineItems = (invoice) => {
    const items = [];
    if (invoice.general && Array.isArray(invoice.general)) items.push(...invoice.general);
    if (invoice.account_general && Array.isArray(invoice.account_general)) items.push(...invoice.account_general);
    return items;
  }

  const getLineDescription = (item) => {
    if (item.product && item.product.name) return safeText(item.product.name);
    if (item.description) return safeText(item.description);
    return safeText(item.account_head_name);
  }

  const getTableRow = (invoice) => {
    const lineItems = getLineItems(invoice);
    if (!lineItems.length) return [];
    return lineItems.map((item, index) => {
      const productName = getLineDescription(item);
      const quantity = item.quantity || 0;
      const unit = getUnitName(item.product);
      let unitPrice = item.rate || 0;
      let amount = quantity*unitPrice;

      if (invoice.vat_type === 'vat_inclusive' && quantity > 0){
          unitPrice = item.rate - (item.vat_amount / quantity)
          amount = item.amount;
      }

      return [
        { text: (index + 1) + '.', alignment: 'center', fontSize: 8 },
        { text: productName, alignment: 'left', fontSize: 8 },
        { text: unit, alignment: 'center', fontSize: 8 },
        { text: quantity.toString(), alignment: 'right', fontSize: 8 },
        { text: unitPrice.toFixed(2), alignment: 'right', fontSize: 8 },
        { text: commaFormat(amount), alignment: 'right', fontSize: 8 },
        { text: '0.00', alignment: 'right', fontSize: 8 },
        { text: '0.00', alignment: 'right', fontSize: 8 },
        { text: safeText(item.vat), alignment: 'right', fontSize: 8 },
        { text: commaFormat(item.vat_amount), alignment: 'right', fontSize: 8 },
        { text: commaFormat(item.total_amount), alignment: 'right', fontSize: 8 }
      ];
    });
  };

  const getTableTotal = (invoice) => {
    const lineItems = getLineItems(invoice);
    if (!lineItems.length) return [];

    let totalQuantity = 0;
    let totalAmount = 0;
    let totalVatAmount = 0;
    let totalWithVat = 0;

    lineItems.forEach(item => {
      const quantity = item.quantity || 0;
      const unitPrice = item.rate || 0;
      const vatAmount = item.vat_amount || 0;
      const totalLineAmount = item.total_amount || 0;
      let amount = quantity * unitPrice;

      if (invoice.vat_type === 'vat_inclusive'){
         amount = item.amount || 0
      }

      totalQuantity += quantity;
      totalAmount += amount;
      totalVatAmount += vatAmount;
      totalWithVat += totalLineAmount;
    });

    return [
        { text: 'Total', colSpan:3, style: 'tableHeaderSmall', bold: true}, {}, {},
        { text: totalQuantity.toString(), alignment: 'right', fontSize: 8, bold: true },
        { text: '', alignment: 'right', fontSize: 8 },
        { text: commaFormat(totalAmount), alignment: 'right', fontSize: 8, bold: true },
        { text: '0.00', alignment: 'right', fontSize: 8, bold: true },
        { text: '0.00', alignment: 'right', fontSize: 8, bold: true },
        { text: '', alignment: 'right', fontSize: 8 },
        { text: commaFormat(totalVatAmount), alignment: 'right', fontSize: 8, bold: true },
        { text: commaFormat(totalWithVat), alignment: 'right', fontSize: 8, bold: true }
    ];
  };

  const getTableHead = () => {
    return [
      { text: 'S.L\nNO', style: 'tableHeaderSmall' },
      { text: 'Goods/Service Description\n(Incases with Brand Name)', style: 'tableHeaderSmall' },
      { text: 'Unit of\nSupply', style: 'tableHeaderSmall' },
      { text: 'Qty', style: 'tableHeaderSmall' },
      { text: 'Unit\nValue 1', style: 'tableHeaderSmall' },
      { text: 'Total\nValue 1', style: 'tableHeaderSmall' },
      { text: 'Amount\nSupp.\nDuty (%)', style: 'tableHeaderSmall' },
      { text: 'Amount\nSupp.\nDuty', style: 'tableHeaderSmall' },
      { text: 'VAT rate/\nSpecific\nTax', style: 'tableHeaderSmall' },
      { text: 'Value Added\nTax rate/\nSpecific Tax\nAmount', style: 'tableHeaderSmall' },
      { text: 'Value all\nkinds of\nDuty & Tax', style: 'tableHeaderSmall' }
    ];
  };

  const getTableHeadNumber = () => {
    return getTableHead().map((column, index) => {
      return { text: '(' + (index + 1) + ')', style: 'tableHeaderNumber' };
    });
  };

  const getHeader = (imagePath) => {
    return {
    margin: [30, 30, 30, 0],
    stack: [
      {
        columns: [
          {
            image: imagePath,
            width: 50,
            height: 50,
          },
          {
            width: '*',
            stack: [
              {
                text: "Government of the People's Republic of Bangladesh",
                style: 'headerCenter',
              },
              {
                text: 'National Board of Revenue',
                style: 'headerCenter',
                margin: [0, 5, 0, 2],
              },
              {
                text: 'Tax Challan',
                style: 'subHeaderCenter',
                margin: [0, 0, 0, 3],
                fontSize: 10
              },

              {
                text: '[See Clauses (C) and (f) of Sub-Rule (1) of Rule 40]',
                style: 'subHeaderCenter',
                margin: [0, 0, 10, 0],
                fontSize: 10
              },
            ],
          },
          {
            width: 70,
            margin: [0, 20, 0, 0],
            table: {
              widths: ['*'],
              body: [
                [
                  {
                    text: 'MUSHAK-6.3',
                    alignment: 'center',
                    fontSize: 9
                  },
                ],
              ],
            },
            layout: {
              hLineWidth: () => 1,
              vLineWidth: () => 1,
              hLineColor: () => '#000',
              vLineColor: () => '#000',
            },
          },
        ],
      },
    ],
    };
  };

  const getHeadTable = (invoice) => {
    return {
      layout: 'noBorders',
      table: {
        fontSize: 13,
        widths: ['50%', '50%'],
        body: [
          [
            { text: 'Name of Purchaser: ' + getContactField(invoice.contact, 'name'), alignment: 'left', fontSize: 10},
            { text: 'Invoice No: ' + safeText(invoice.bill_number), alignment: 'right', fontSize: 10 }
          ],
          [
            { text: 'BIN of Purchaser: ' + getContactField(invoice.contact, 'bin_no'), alignment: 'left', fontSize: 10},
            { text: 'Date of Issue: ' + formatDate(new Date(invoice.date)).split(' ')[0], alignment: 'right', fontSize: 10 }
          ],
          [
            {
              text: 'Address of Purchaser: ' + getContactAddress(invoice.contact), alignment: 'left', fontSize: 10, margin: [0, 0, 0, 3]
            },
            { text: 'Time of Issue: ' + safeText(invoice.updated_at_time), alignment: 'right', fontSize: 10 }
          ],
          [
            { text: 'Destination of Supply: ' + safeText(invoice.destination_address), colSpan: 2, alignment: 'left', fontSize: 10},
            {}
          ],
          [
            { text: 'Nature & Number of Vehicle: ' + safeText(invoice.transport_reference), colSpan: 2, alignment: 'left', fontSize: 10},
            {}
          ]
        ]
      },
      margin: [0, 5, 0, 10]
    }
  }

  const getFooter = () => {
    return {
      margin: [30, 0, 30, 20],
      columns: [
        {
          width: '100%',
          text:
            '1. Value except all kinds of Tax.\n' +
            '2. Made under S.R.O No -135-Law/2024/241-Mushak, Date: 27 May, 2024.',
          style: 'footerNote'
        }
      ]
    };
  }

  const getUnitName = (product) => {
    if (!product || !product.description) return '';
    if (!product.description.unit) return '';
    return safeText(product.description.unit.name);
  }

  const getContactField = (contact, field) => {
    if (!contact) return '';
    return safeText(contact[field]);
  }

  const getContactAddress = (contact) => {
    if (!contact) return '';
    return getProfileItemByField(contact.profile_items, 'billing_address');
  }

  const safeText = (value) => {
    if (value === null || value === undefined) return '';
    if (value === 'null' || value === 'undefined') return '';
    return String(value);
  }

  const getProfileItemByField = (profile_items, keyValue) => {
    if (!profile_items || !Array.isArray(profile_items)) return '';
    const item = profile_items.find(item => item.field === keyValue);
    if (!item) return '';
    return safeText(item.value);
  }

  const styles = {
    headerCenter: {
      fontSize: 14,
      alignment: "center",
    },
    subHeaderCenter: {
      fontSize: 11,
      alignment: "center"
    },
    smallCenter: {
      fontSize: 9,
      alignment: "center",
    },
    tableHeader: {
      bold: true,
      decoration: "underline"
    },
    tableHeaderSmall: {
      fontSize: 8,
      alignment: "center",
      lineHeight: 1.1
    },
    tableHeaderNumber: {
      fontSize: 7,
      alignment: "center",
      bold: true
    },
    footerNote: {
      fontSize: 8,
    }
  };

  return {
    exportToPdfMushakEn
  }
}

export default printMushak63En;
